import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useFormikContext } from 'formik';
import { useGoogleAutocomplete } from '@app/core';
import { Select, SelectProps } from './Select';
import { useOptionalField } from '../FormikFields';

const useStyles = createUseStyles((theme: Theme) => ({
  main: {},
}));

interface Props<IsMulti extends boolean> extends SelectProps<IsMulti> {
  name?: string;
  label?: string;
}

export const SelectGeolocation = <IsMulti extends boolean>({
  name,
  label,
  ...props
}: Props<IsMulti>) => {
  const classes = useStyles();
  const [results, setResults] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const {
    field: { value: formikValue },
  } = useOptionalField({ name });

  const placeService = useGoogleAutocomplete();

  const autocomplete = useCallback(
    debounce(
      async (text: string) => {
        if (!placeService) {
          return;
        }
        const res = await placeService.getPlacePredictions({
          input: text,
          types: ['(cities)'],
        });
        setResults(res.predictions);
      },
      300,
      {
        leading: true,
      },
    ),
    [setResults, placeService],
  );

  const options = useMemo(() => {
    const options = results.map((result) => {
      return result.description;
    });
    if (!formikValue) {
      return options;
    }
    if (options.includes(formikValue)) {
      return options;
    }
    return [formikValue, ...options];
  }, [results]);

  const onTypeText = useCallback(
    (inputValue: string) => {
      if (inputValue.length === 0) {
        return;
      }
      autocomplete(inputValue);
    },
    [autocomplete],
  );

  return (
    <div className={classes.main}>
      <Select
        {...props}
        isClearable
        label={label}
        name={name}
        key={name}
        options={options}
        onInputChange={onTypeText}
      />
    </div>
  );
};
